import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import FolderOffOutlinedIcon from "@mui/icons-material/FolderOffOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";

/* ---------------------------------------------------------
   Design tokens — kept in sync with Dashboard.jsx /
   SitemapGroups.jsx / Urls.jsx.
--------------------------------------------------------- */
const COLOR = {
  bg: "#F5F6F4",
  surface: "#FFFFFF",
  ink: "#161A1D",
  inkMuted: "#6B7280",
  inkFaint: "#9AA1A9",
  line: "#E4E7E7",
  accent: "#0B7285",
  accentSoft: "#E3F1F1",
  success: "#2F9E44",
  successSoft: "#E9F7EC",
  warning: "#E8890C",
  warningSoft: "#FDF1E1",
};
const FONT_DISPLAY = "'Space Grotesk', sans-serif";
const FONT_MONO = "'JetBrains Mono', monospace";

const FONT_IMPORT = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
`;

const ENDPOINTS = {
  groups: "/api/v1/sitemap/groups?source=wordpress",
  sync: "/api/v1/sitemap/wordpress-sync",
};

export default function WordpressSync({ onNavigateToGroups }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedGroups, setSelectedGroups] = useState([]);
  const [syncingSelected, setSyncingSelected] = useState(false);
  const [syncingRowId, setSyncingRowId] = useState(null);

  const [syncAllOpen, setSyncAllOpen] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(ENDPOINTS.groups);
      setGroups(data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load WordPress groups.");
    } finally {
      setLoading(false);
    }
  };

  const anySyncInFlight = syncingSelected || syncingAll || Boolean(syncingRowId) ;

  const handleSelectAll = (e) => {
    setSelectedGroups(e.target.checked ? groups.map((g) => g.id) : []);
  };

  const handleGroupSelect = (id) => {
    setSelectedGroups((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const runSync = async (body) => {
    const { data } = await axios.post(ENDPOINTS.sync, body);

    const failedGroups = data.data?.filter(item => !item.success) || [];

    if (failedGroups.length > 0) {
      toast.warning(
        failedGroups.map(item => `${item.group}: ${item.message}`).join("\n")
      );
    } else {
      toast.success(data.message || "WordPress sync completed successfully.");
    }
  };

  const syncGroup = async (group) => {
    setSyncingRowId(group.id);
    try {
      await runSync({ syncAll: false, groupIds: [group.id] });
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to sync "${group.name}".`);
    } finally {
      setSyncingRowId(null);
    }
  };

  const syncSelected = async () => {
    if (selectedGroups.length === 0) {
      toast.warning("Select at least one group first.");
      return;
    }
    setSyncingSelected(true);
    try {
      await runSync({ syncAll: false, groupIds: selectedGroups });
    } catch (error) {
      toast.error(error.response?.data?.message || "WordPress sync failed.");
    } finally {
      setSyncingSelected(false);
    }
  };

  const confirmSyncAll = async () => {
    setSyncingAll(true);
    try {
      await runSync({ syncAll: true });
      setSyncAllOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "WordPress sync failed.");
    } finally {
      setSyncingAll(false);
    }
  };

  const allSelected = groups.length > 0 && selectedGroups.length === groups.length;
  const someSelected = selectedGroups.length > 0 && !allSelected;
  const hasGroups = groups.length > 0;

  const selectedCountLabel = useMemo(() => {
    if (selectedGroups.length === 0) return null;
    return `${selectedGroups.length} group${selectedGroups.length > 1 ? "s" : ""} selected`;
  }, [selectedGroups]);

  return (
    <>
      <style>{FONT_IMPORT}</style>

      <Card
        sx={{
          borderRadius: "14px",
          border: `1px solid ${COLOR.line}`,
          boxShadow: "none",
          bgcolor: COLOR.surface,
        }}
      >
        <CardContent sx={{ p: "24px" }}>
          {/* Header */}
          <Box mb={2.5}>
            <Typography
              sx={{
                fontFamily: FONT_DISPLAY,
                fontWeight: 600,
                fontSize: "19px",
                color: COLOR.ink,
                letterSpacing: "-0.01em",
              }}
            >
              WordPress Synchronization
            </Typography>
            <Typography sx={{ fontSize: "13px", color: COLOR.inkMuted, mt: "2px" }}>
              Select the groups you want to synchronize with WordPress.
            </Typography>
          </Box>

          {/* Setup guidance — always visible, since it's easy to
              land here before creating any groups. */}
          <Alert
            icon={<InfoOutlinedIcon sx={{ fontSize: 20 }} />}
            severity="info"
            sx={{
              borderRadius: "10px",
              mb: 3,
              bgcolor: COLOR.accentSoft,
              color: COLOR.ink,
              "& .MuiAlert-icon": { color: COLOR.accent },
            }}
            action={
              onNavigateToGroups ? (
                <Button
                  size="small"
                  onClick={onNavigateToGroups}
                  sx={{ textTransform: "none", fontWeight: 600, color: COLOR.accent }}
                >
                  Go to groups
                </Button>
              ) : undefined
            }
          >
            Don't see a group you're expecting? Create a sitemap group first —
            you can name it and set its slug to whatever you like — then come
            back here to sync it with WordPress.
          </Alert>

          {/* Bulk action bar */}
          {hasGroups && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={2}
              mb={2}
            >
              <Box display="flex" alignItems="center" gap="10px">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleSelectAll}
                  disabled={anySyncInFlight}
                  sx={{
                    color: COLOR.inkFaint,
                    "&.Mui-checked": { color: COLOR.accent },
                    "&.MuiCheckbox-indeterminate": { color: COLOR.accent },
                  }}
                />
                <Typography sx={{ fontSize: "13.5px", color: COLOR.inkMuted }}>
                  {selectedCountLabel || "Select all"}
                </Typography>
              </Box>

              <Box display="flex" gap="10px" flexWrap="wrap">
                <Button
                  variant="outlined"
                  onClick={syncSelected}
                  disabled={anySyncInFlight || selectedGroups.length === 0}
                  startIcon={
                    syncingSelected ? (
                      <CircularProgress size={14} sx={{ color: COLOR.accent }} />
                    ) : (
                      <SyncOutlinedIcon sx={{ fontSize: 17 }} />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: COLOR.line,
                    color: COLOR.ink,
                    "&:hover": { borderColor: COLOR.accent, bgcolor: COLOR.accentSoft },
                  }}
                >
                  {syncingSelected ? "Syncing…" : "Sync selected"}
                </Button>

                <Button
                  variant="contained"
                  disableElevation
                  onClick={() => setSyncAllOpen(true)}
                  disabled={anySyncInFlight}
                  startIcon={<SyncOutlinedIcon sx={{ fontSize: 17 }} />}
                  sx={{
                    bgcolor: COLOR.accent,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { bgcolor: "#095e6b" },
                  }}
                >
                  Sync all
                </Button>
              </Box>
            </Box>
          )}

          {/* Table */}
          <TableContainer sx={{ border: `1px solid ${COLOR.line}`, borderRadius: "10px" }}>
            <Table>
              <TableHead>
                <TableRow sx={{ "& th": { bgcolor: COLOR.bg } }}>
                  <TableCell padding="checkbox" sx={{ borderBottom: `1px solid ${COLOR.line}` }} />
                  {["Group", "Slug", "Type", "URLs", ""].map((head, i) => (
                    <TableCell
                      key={i}
                      align={head === "" ? "center" : "left"}
                      sx={{
                        fontSize: "11.5px",
                        fontWeight: 600,
                        color: COLOR.inkFaint,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        borderBottom: `1px solid ${COLOR.line}`,
                      }}
                    >
                      {head || "Action"}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6, border: 0 }}>
                      <CircularProgress size={26} sx={{ color: COLOR.accent }} />
                    </TableCell>
                  </TableRow>
                ) : !hasGroups ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6, border: 0 }}>
                      <Box display="flex" flexDirection="column" alignItems="center" gap="8px">
                        <FolderOffOutlinedIcon sx={{ fontSize: 28, color: COLOR.inkFaint }} />
                        <Typography sx={{ fontSize: "14px", fontWeight: 500, color: COLOR.ink }}>
                          No WordPress groups yet
                        </Typography>
                        <Typography sx={{ fontSize: "13px", color: COLOR.inkMuted, maxWidth: 340 }}>
                          Create a group with the slug you want, then it'll
                          show up here ready to sync.
                        </Typography>
                        {onNavigateToGroups && (
                          <Button
                            size="small"
                            onClick={onNavigateToGroups}
                            sx={{ textTransform: "none", fontWeight: 600, color: COLOR.accent, mt: "4px" }}
                          >
                            Create a group
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  groups.map((group) => {
                    const isRowSyncing = syncingRowId === group.id;
                    return (
                      <TableRow key={group.id} hover sx={{ "& td": { borderBottom: `1px solid ${COLOR.line}` } }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedGroups.includes(group.id)}
                            onChange={() => handleGroupSelect(group.id)}
                            disabled={anySyncInFlight}
                            sx={{
                              color: COLOR.inkFaint,
                              "&.Mui-checked": { color: COLOR.accent },
                            }}
                          />
                        </TableCell>

                        <TableCell sx={{ fontSize: "13.5px", fontWeight: 500, color: COLOR.ink }}>
                          {group.name}
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${group.slug}.xml`}
                            sx={{
                              borderColor: COLOR.line,
                              color: COLOR.inkMuted,
                              fontFamily: FONT_MONO,
                              fontSize: "11.5px",
                            }}
                          />
                        </TableCell>

                        <TableCell sx={{ fontSize: "13.5px", fontWeight: 500, color: COLOR.ink }}>
                          {group.type}
                        </TableCell>

                        <TableCell sx={{ fontFamily: FONT_MONO, fontSize: "13px", color: COLOR.inkMuted }}>
                          {group.url_count ?? "—"}
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip title={`Sync "${group.name}" now`}>
                            <span>
                              <Button
                                size="small"
                                onClick={() => syncGroup(group)}
                                disabled={anySyncInFlight}
                                startIcon={
                                  isRowSyncing ? (
                                    <CircularProgress size={13} sx={{ color: COLOR.accent }} />
                                  ) : (
                                    <SyncOutlinedIcon sx={{ fontSize: 15 }} />
                                  )
                                }
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  fontSize: "12.5px",
                                  color: COLOR.accent,
                                  "&:hover": { bgcolor: COLOR.accentSoft },
                                }}
                              >
                                {isRowSyncing ? "Syncing…" : "Sync"}
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Sync All confirmation */}
      <Dialog
        open={syncAllOpen}
        onClose={() => !syncingAll && setSyncAllOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "14px" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <WarningAmberOutlinedIcon sx={{ color: COLOR.warning }} />
          Sync all groups?
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ fontSize: "14px", color: COLOR.ink, mb: 2 }}>
            This will re-sync every WordPress group at once, including any not
            currently selected.
          </Typography>

          <Alert
            icon={<AccessTimeOutlinedIcon sx={{ fontSize: 20 }} />}
            severity="warning"
            sx={{
              borderRadius: "10px",
              bgcolor: COLOR.warningSoft,
              color: COLOR.ink,
              "& .MuiAlert-icon": { color: COLOR.warning },
            }}
          >
            Depending on how many URLs you have, a full sync can take
            anywhere from <b>30 minutes to an hour</b>. It runs in the
            background, so it's safe to navigate away once it starts.
          </Alert>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setSyncAllOpen(false)}
            disabled={syncingAll}
            sx={{ textTransform: "none", color: COLOR.inkMuted }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={confirmSyncAll}
            disabled={syncingAll}
            sx={{
              bgcolor: COLOR.warning,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#c2730a" },
            }}
          >
            {syncingAll ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Start sync"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}